import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PaymentMethodPickerBlockConfig } from '@front-platform/content-builder';
import { paymentMethodPickerBlockResolveConfig } from '@front-platform/content-builder';
import dayjs from 'dayjs';
import { NavigationStart, Router } from '@angular/router';
import { from, of, Subject, timer, BehaviorSubject } from 'rxjs';
import { Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';

import { OrderKeys } from '@config/keys/order.keys';

import {
  PAYMENT_STORE_BUTTON_INITIAL_STATE,
  PAYMENT_STORE_STATUS_INITIAL_STATE,
} from '@store/payment/payment.state';

import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';

import { OrderStoreSelector } from '@common/selectors/order.selector';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { PaymentStoreSelector } from '@common/selectors/payment.selector';
import { TransactionStoreSelector } from '@common/selectors/transaction.selector';

import { Paths } from '@config/paths.config';
import { IPaymentMethod, PaymentMethodHandles } from '@config/payment-methods.config';
import { ORDER_ALREADY_PAID, TRANSACTION_EXCEEDS_TOTAL } from '@config/errors.config';

import { NativeService } from '@core/services/native/native.service';
import { PaymentService } from '@core/services/payment/payment.service';
import { ErrorService } from '@core/services/error-service/error.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { TerminalAbortService } from '@core/services/yavin-terminal/terminal-abort.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { SELECT_CART_HAS_CHECKED_FOR_ASSOCIATED_PAID_ORDER } from '@store/slices/cart-avoid-duplication-order';

import {
  PAYMENT_METHOD_PICKER_BLOCK_DATA_SOURCE,
  PaymentMethodPickerBlockDataSource,
} from './payment-method-picker-block-view-model';
import { providePaymentMethodPickerBlockDataSource } from './payment-method-picker-block-data-source.providers';

export interface PaymentMethodPickerBlockRuntimeConfig {
  'terminal_instruction_message'?: string;
}

export type PaymentMethodPickerBlockConfigInput = Partial<
  Omit<PaymentMethodPickerBlockConfig, keyof PaymentMethodPickerBlockRuntimeConfig> &
    PaymentMethodPickerBlockRuntimeConfig
>;
export type PaymentMethodPickerBlockResolvedConfig = Omit<
  PaymentMethodPickerBlockConfig,
  keyof PaymentMethodPickerBlockRuntimeConfig
> &
  Partial<PaymentMethodPickerBlockRuntimeConfig>;
export interface PaymentMethodPickerBlockContract extends ConfigComponentContract<
  PaymentMethodPickerBlockConfigInput,
  PaymentMethodPickerBlockResolvedConfig
> {}

@Component({
  selector: 'app-payment-method-picker-block',
  templateUrl: './payment-method-picker-block.component.html',
  styleUrls: ['./payment-method-picker-block.component.scss'],
  standalone: false,
  providers: [...providePaymentMethodPickerBlockDataSource()],
})
export class PaymentMethodPickerBlockComponent
  implements OnInit, OnDestroy, PaymentMethodPickerBlockContract
{
  readonly config = input<PaymentMethodPickerBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PaymentMethodPickerBlockConfig,
    PaymentMethodPickerBlockRuntimeConfig
  >(paymentMethodPickerBlockResolveConfig, () => ({ config: this.config() }));
  readonly bottomMargin = computed(
    () => this.resolvedConfig()['bottom_margin'] as unknown as 'small' | 'large',
  );
  readonly terminalInstructionMessage = computed(
    () => this.resolvedConfig()['terminal_instruction_message'] as unknown as string,
  );
  readonly headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );

  private readonly router = inject(Router);
  private readonly orderSelector = inject(OrderStoreSelector);
  private readonly basketSelector = inject(BasketStoreSelector);
  private readonly errorService = inject(ErrorService);
  private readonly paymentSelector = inject(PaymentStoreSelector);
  private readonly orderDispatcher = inject(OrderStoreDispatcher);
  private readonly paymentService = inject(PaymentService);
  private readonly paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly transactionSelector = inject(TransactionStoreSelector);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly store = inject(Store);
  private readonly terminalAbortService = inject(TerminalAbortService);
  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly blockDataSource = inject<PaymentMethodPickerBlockDataSource>(
    PAYMENT_METHOD_PICKER_BLOCK_DATA_SOURCE,
  );

  /** Real payment panels hit external APIs on init — never mount them in CB2 preview. */
  protected readonly isPreview = this.previewState.isActive();

  private readonly _destroyerRef = new Subject<boolean>();
  private readonly paymentPageEnterAt$ = new BehaviorSubject<Date | null>(null);

  /** Prevents switching methods while an order/payment intent is being created. */
  private readonly paymentMethodSwitchLockTimeoutMs = 5000;
  private readonly paymentMethodSwitchLockGraceMs = 150;
  private paymentMethodSwitchLockSafetyTimer: ReturnType<typeof setTimeout> | null = null;
  private paymentMethodSwitchLockGraceTimer: ReturnType<typeof setTimeout> | null = null;
  isPaymentMethodSwitchLocked = false;

  order$ = this.orderSelector.selectValidation$;
  isPaymentRetryInProgress$ = this.paymentSelector.selectStatus.pipe(map(s => s.isRetryInProgress));

  get isPaymentMethodSelectionLocked(): boolean {
    return (
      this.isPaymentMethodSwitchLocked || this.status.isLoading || this.status.isRetryInProgress
    );
  }

  hasCheckedAssociatedPaidOrder = toSignal(
    this.store.select(SELECT_CART_HAS_CHECKED_FOR_ASSOCIATED_PAID_ORDER),
    { initialValue: false },
  );

  readonly orderKeys = OrderKeys;
  remainingTime = signal('');
  remainingMinutes = signal<number | null>(null);
  orderFinalPrice: number | null = null;

  displayRemainingMinutes = computed(() => {
    const paymentMethods = this.availablePaymentMethods ?? [];
    if (
      paymentMethods.length === 1 &&
      paymentMethods[0].handle === PaymentMethodHandles.OnsiteWithoutDeposit
    ) {
      return false;
    }
    const bornePaymentMethodHandles = [
      PaymentMethodHandles.AuresEtkTerminal,
      PaymentMethodHandles.YavinTerminal,
      PaymentMethodHandles.NeptingTerminal,
      PaymentMethodHandles.TcposSco,
      PaymentMethodHandles.VivaWalletTerminal,
    ];
    if (paymentMethods.some(pm => bornePaymentMethodHandles.includes(pm.handle))) {
      return false;
    }
    return this.remainingMinutes() !== null;
  });

  availablePaymentMethods?: IPaymentMethod[];
  selectedPaymentMethod: IPaymentMethod | null = null;
  isNotched = false;
  isAbortingTerminal = false;
  abortTerminalFailed = false;
  private abortAction: 'switchMethod' | 'navigate' | null = null;
  private abortTargetMethod: IPaymentMethod | null = null;
  private pendingNavigationUrl: string | null = null;
  private _navigatingToSelf = false;
  errorMessages: string[] = [];
  error: string | null = null;
  amount: number | null = null;
  button = PAYMENT_STORE_BUTTON_INITIAL_STATE;
  status = PAYMENT_STORE_STATUS_INITIAL_STATE;

  get hasErrorMessages(): boolean {
    return this.errorMessages?.length > 0;
  }

  ngOnInit(): void {
    const isPreview = this.previewState.isActive();
    const parsedUrl = this.router.parseUrl(this.router.url);

    this.blockDataSource.vm$.pipe(takeUntil(this._destroyerRef)).subscribe(vm => {
      const availablePaymentMethods = [...vm.availablePaymentMethods];
      this.availablePaymentMethods = availablePaymentMethods;
      this.orderFinalPrice = vm.orderFinalPrice;
      if (vm.remainingMinutes !== null) {
        this.remainingMinutes.set(vm.remainingMinutes);
      }

      if (!isPreview) {
        this.paymentService.prepareStripeForCheckout(availablePaymentMethods);
      }

      if (availablePaymentMethods.length === 1) {
        this.selectedPaymentMethod = availablePaymentMethods[0];
      } else if (parsedUrl.queryParams?.payment_method === PaymentMethodHandles.Edenred) {
        this.selectedPaymentMethod =
          availablePaymentMethods.find(pm => pm.handle === PaymentMethodHandles.Edenred) || null;
      } else {
        const selectedHandle = this.selectedPaymentMethod?.handle;
        this.selectedPaymentMethod =
          availablePaymentMethods.find(pm => pm.handle === selectedHandle) ?? null;
      }
    });

    if (isPreview) {
      // Preview only needs the method list + total; skip store side-effects / redirects.
      return;
    }

    this.paymentSelector.select
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(({ amount, button, error, status }) => {
        this.status = status;
        this.button = button;
        this.amount = amount;
        this.error = error;
      });

    this.paymentSelector.selectStatus
      .pipe(
        map(status => status.isLoading),
        distinctUntilChanged(),
        takeUntil(this._destroyerRef),
      )
      .subscribe(isLoading => {
        if (isLoading) {
          // A panel claimed the loading state — wait for it to finish.
          this.clearPaymentMethodSwitchLockGraceTimer();
          return;
        }
        if (this.isPaymentMethodSwitchLocked) {
          this.unlockPaymentMethodSwitch();
        }
      });

    this.paymentDispatcher.resetUI();

    this.errorService
      .getErrorMessage$()
      .pipe(
        takeUntil(this._destroyerRef),
        tap(messages => (this.errorMessages = messages)),
      )
      .subscribe();

    from(NativeService.isNotched()).subscribe(isNotched => {
      this.isNotched = isNotched;
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntil(this._destroyerRef),
      )
      .subscribe(event => {
        if (this.isAbortingTerminal || this._navigatingToSelf) return;
        const abort$ = this.terminalAbortService.abortIfNeeded$();
        if (!abort$) return;

        this.pendingNavigationUrl = event.url;
        this.abortAction = 'navigate';
        this.isAbortingTerminal = true;
        this.abortTerminalFailed = false;

        this._navigatingToSelf = true;
        void this.router.navigateByUrl(this.router.url, { replaceUrl: true }).then(() => {
          this._navigatingToSelf = false;
        });

        abort$.pipe(takeUntil(this._destroyerRef)).subscribe(success => {
          if (success) {
            this.completeContinue();
          } else {
            this.abortTerminalFailed = true;
          }
        });
      });

    this.redirectIfBasketShareOrderIsAlreadyPaid();

    // TODO: Change this by a selector ?
    if (this.orderSelector.active) {
      this.orderDispatcher.resetActive();
    }

    // Start countdown from page entry, resets each time component is instantiated
    this.paymentPageEnterAt$.next(new Date());
    this.triggerTimeoutCreatedOrder();
  }

  getPanelData(paymentMethod: IPaymentMethod): object {
    const base = paymentMethod.data ?? {};
    const message = this.terminalInstructionMessage();
    if (
      message != null &&
      (paymentMethod.handle === PaymentMethodHandles.YavinTerminal ||
        paymentMethod.handle === PaymentMethodHandles.NeptingTerminal ||
        paymentMethod.handle === PaymentMethodHandles.TcposSco ||
        paymentMethod.handle === PaymentMethodHandles.VivaWalletTerminal)
    ) {
      return { ...base, terminalInstructionMessage: message };
    }
    return base;
  }

  selectPaymentMethod(paymentMethod: IPaymentMethod): void {
    if (this.isPaymentMethodSelectionLocked) {
      return;
    }
    this.isPaymentRetryInProgress$
      .pipe(
        take(1),
        tap(isPaymentRetryInProgress => {
          if (isPaymentRetryInProgress || this.isPaymentMethodSelectionLocked) {
            return;
          }
          const abort$ = this.terminalAbortService.abortIfNeeded$();
          if (abort$) {
            this.abortTargetMethod =
              this.selectedPaymentMethod === paymentMethod ? null : paymentMethod;
            this.abortAction = 'switchMethod';
            this.isAbortingTerminal = true;
            this.abortTerminalFailed = false;
            abort$.pipe(takeUntil(this._destroyerRef)).subscribe(success => {
              if (success) {
                this.completeContinue();
              } else {
                this.abortTerminalFailed = true;
              }
            });
            return;
          }
          this.doSelectPaymentMethod(paymentMethod);
        }),
      )
      .subscribe();
  }

  protected forceContinue(): void {
    this.completeContinue();
  }

  private completeContinue(): void {
    this.isAbortingTerminal = false;
    this.abortTerminalFailed = false;
    if (this.abortAction === 'navigate') {
      const target = this.pendingNavigationUrl;
      this.abortAction = null;
      this.pendingNavigationUrl = null;
      this.selectedPaymentMethod = null;
      this.paymentDispatcher.resetUI();
      if (target) {
        this._navigatingToSelf = true;
        void this.router.navigateByUrl(target).then(() => {
          this._navigatingToSelf = false;
        });
      }
    } else {
      this.abortAction = null;
      if (this.abortTargetMethod === null) {
        this.selectedPaymentMethod = null;
        this.paymentDispatcher.resetUI();
      } else {
        this.selectedPaymentMethod = this.abortTargetMethod;
      }
      this.abortTargetMethod = null;
    }
  }

  private doSelectPaymentMethod(paymentMethod: IPaymentMethod): void {
    if (this.selectedPaymentMethod === paymentMethod) {
      this.selectedPaymentMethod = null;
      this.paymentDispatcher.resetUI();
      return;
    }
    this.lockPaymentMethodSwitch();
    this.selectedPaymentMethod = paymentMethod;
  }

  private lockPaymentMethodSwitch(): void {
    this.isPaymentMethodSwitchLocked = true;
    this.clearPaymentMethodSwitchLockTimers();

    // Panels that don't create an order on init never set isLoading — unlock quickly.
    this.paymentMethodSwitchLockGraceTimer = setTimeout(() => {
      this.paymentMethodSwitchLockGraceTimer = null;
      if (!this.status.isLoading) {
        this.unlockPaymentMethodSwitch();
      }
    }, this.paymentMethodSwitchLockGraceMs);

    // Safety: never leave the list permanently locked if a request hangs.
    this.paymentMethodSwitchLockSafetyTimer = setTimeout(() => {
      this.paymentMethodSwitchLockSafetyTimer = null;
      if (this.status.isLoading) {
        this.paymentDispatcher.updateStatus({ isLoading: false });
      }
      this.unlockPaymentMethodSwitch();
    }, this.paymentMethodSwitchLockTimeoutMs);
  }

  private unlockPaymentMethodSwitch(): void {
    this.isPaymentMethodSwitchLocked = false;
    this.clearPaymentMethodSwitchLockTimers();
  }

  private clearPaymentMethodSwitchLockGraceTimer(): void {
    if (this.paymentMethodSwitchLockGraceTimer) {
      clearTimeout(this.paymentMethodSwitchLockGraceTimer);
      this.paymentMethodSwitchLockGraceTimer = null;
    }
  }

  private clearPaymentMethodSwitchLockTimers(): void {
    this.clearPaymentMethodSwitchLockGraceTimer();
    if (this.paymentMethodSwitchLockSafetyTimer) {
      clearTimeout(this.paymentMethodSwitchLockSafetyTimer);
      this.paymentMethodSwitchLockSafetyTimer = null;
    }
  }

  triggerPayment(): void {
    this.paymentService.triggerPayment();
  }

  ngOnDestroy(): void {
    this.clearPaymentMethodSwitchLockTimers();
    this.remainingTime.set('');
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  private redirectIfBasketShareOrderIsAlreadyPaid(): void {
    const basket = this.basketSelector.basket.id;
    if (basket) {
      this.transactionSelector.selectError
        .pipe(
          takeUntil(this._destroyerRef),
          map(error => {
            if (error === ORDER_ALREADY_PAID || error === TRANSACTION_EXCEEDS_TOTAL) {
              this.router.navigate([this.routerHelper.translateRoute(`${Paths.GroupPayment}`)]);
            }
          }),
        )
        .subscribe();
    }
  }

  private triggerTimeoutCreatedOrder(): void {
    this.paymentPageEnterAt$
      .pipe(
        takeUntil(this._destroyerRef),
        switchMap(createdAt => {
          if (!createdAt) {
            return of(null);
          }
          const orderTimeout = dayjs(createdAt).add(10, 'minute');
          return timer(0, 1000).pipe(
            map(() => {
              const now = dayjs();
              const diffInSeconds = orderTimeout.diff(now, 'second');
              if (diffInSeconds <= 0) {
                this.router.navigate([this.routerHelper.translateRoute(`/${Paths.Cart}`)]);
                return '';
              } else {
                const hours = Math.floor(diffInSeconds / 3600);
                const minutes = Math.floor((diffInSeconds % 3600) / 60);
                const seconds = diffInSeconds % 60;
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
              }
            }),
            takeWhile(remainingTime => remainingTime !== '', true),
          );
        }),
      )
      .subscribe(remainingTime => {
        if (remainingTime) {
          this.remainingTime.set(remainingTime);
          // Update remaining minutes as an integer (floor), based on HH:mm:ss string
          const parts = remainingTime.split(':');
          if (parts.length === 3) {
            const hours = Number(parts[0]);
            const minutes = Number(parts[1]);
            // Seconds not used for display, but minutes should decrease at the correct time
            this.remainingMinutes.set(hours * 60 + minutes);
          } else {
            this.remainingMinutes.set(null);
          }
        } else {
          this.remainingMinutes.set(null);
        }
      });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PaymentMethodPickerBlockComponent, {
  'keyAliases': {},
});
